/*
 * Helper functions for rendering device properties in various ways.
 */

import React from 'react';
import type { DeviceMetadataPreferencesMap, RenderOptions, MetadataType } from './json';


type Renderer = (value: string) => React.ReactElement;
type PropertyRenderer = { property: string; renderer: Renderer };
type RenderFactory = (options: RenderOptions) => Renderer;


/*
 * Fallback renderer used when a property's type has no registered factory.
 * Renders the raw stored string with no type-specific formatting.
 *
 * Exported for consumers to use directly as a fallback.
 */
const renderDefault = (value: string) => {
  return (<span className={'metadataValue__unknown'}>{value}</span>);
};


/*****************************************************************
 * Renderer Factories
 ****************************************************************/

const makeRenderString = () => (value: string) => {
  try {
    return (<span className={'metadataValue__string'}>{value}</span>);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(error);
    return renderDefault(value);
  }
};


/*****************************************************************
 * Rendering Utilities
 ****************************************************************/

/**
 * Per-type renderer factories — the read-side counterpart of the API's
 * `validators` and `transforms`. Keyed by `MetadataType`; each entry takes a
 * field's `render` options (from the client's preferences config) and returns a
 * renderer that converts a stored string value into a React element for display.
 *
 * Phase 1 is string-only, so rendering is trivially a plain text span. The
 * factory shape exists now so that when `integer`/`decimal` types are added to
 * `MetadataType`, their display logic (formatted numbers, unit suffixes,
 * precision) slots into a pipeline already wired into the device screen rather
 * than being bolted on later. Entries with an unrecognised type are silently
 * skipped by `orderedRenderers` — the property simply doesn't appear.
 *
 * Keep in mind: each renderer receives a value as stored by the backend
 * transformer. If the value doesn't match the expected format, fall back to
 * `renderDefault` rather than throwing.
 */
const renderers: { [key in MetadataType]: RenderFactory } = {
  string: makeRenderString,
};

const orderedRenderers = (metadata: DeviceMetadataPreferencesMap): PropertyRenderer[] => {
  const result: (PropertyRenderer & {'order': number})[] = [];
  for (const [key, value] of Object.entries(metadata)) {
    const factory = renderers[value.type];
    if (factory) {
      result.push({ property: key, renderer: factory(value.render), order: value.order });
    }
  }
  return result.sort((a, b) => a.order - b.order).map(({ property, renderer }) => ({ property, renderer }));
};


export { renderDefault, orderedRenderers };
export type { Renderer, PropertyRenderer };
